import React, { useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";
import {
  LayoutDashboard, Milk, FlaskConical, Receipt, Users, Upload, FileOutput,
  Calculator, Settings2, Server, KeyRound, UserCog, LogOut, Menu, Tags,
  FolderTree, Banknote, MoreHorizontal, Tractor, ShieldAlert, Map, Route, Sliders,
  ShieldCheck, HardDrive, ClipboardList, FileText, Mail, Newspaper,
} from "lucide-react";
import {
  OfflineBanner, UpdateBanner, InstallPromptCard, IosInstallHintDialog,
} from "@/components/app/PwaComponents";

const NavItem = ({ to, icon: Icon, label, onClick }) => (
  <NavLink
    to={to}
    onClick={onClick}
    data-testid={`nav-${to.replace(/\//g, "-").replace(/^-/, "")}`}
    className={({ isActive }) =>
      `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
        isActive
          ? "bg-primary text-primary-foreground"
          : "text-foreground/80 hover:bg-secondary"
      }`
    }
  >
    <Icon className="h-4 w-4 shrink-0" />
    {label}
  </NavLink>
);

const getNav = (rolle) => {
  const portal = [
    { to: "/", icon: LayoutDashboard, label: "Dashboard" },
    { to: "/lieferungen", icon: Milk, label: "Lieferungen" },
    { to: "/milchproben", icon: FlaskConical, label: "Milchproben" },
    { to: "/abrechnungen", icon: Receipt, label: "Abrechnungen" },
    { to: "/portal", icon: Newspaper, label: "Portal" },
  ];
  if (rolle === "lieferant") return { Portal: portal };
  if (rolle === "berater")
    return {
      Portal: [
        portal[0],
        { to: "/lieferanten", icon: Tractor, label: "Lieferanten" },
        { to: "/karte", icon: Map, label: "Karte" },
        { to: "/portal", icon: Newspaper, label: "Portal" },
      ],
    };
  // Basis-Navigation für Admin + Sysadmin
  const nav = {
    Portal: [...portal,
      { to: "/lieferanten", icon: Tractor, label: "Lieferanten" },
      { to: "/karte", icon: Map, label: "Karte" },
    ],
    Betrieb: [
      { to: "/admin/import", icon: Upload, label: "ÖNORM-Import" },
      { to: "/admin/reihungsdateien", icon: FileOutput, label: "Reihungsdateien" },
      { to: "/admin/abrechnungslauf", icon: Calculator, label: "Abrechnungslauf" },
      { to: "/admin/qualitaet", icon: ShieldAlert, label: "Qualität & Sperren" },
      { to: "/admin/touren", icon: Route, label: "Tourenplanung" },
      { to: "/admin/dokumente", icon: FileText, label: "Portal-Inhalte" },
    ],
    Stammdaten: [
      { to: "/admin/lieferanten", icon: Tractor, label: "Lieferanten-Verwaltung" },
      { to: "/admin/milchsorten", icon: Tags, label: "Milchsorten" },
      { to: "/admin/gruppen", icon: FolderTree, label: "Gruppen" },
      { to: "/admin/preismodell", icon: Banknote, label: "Preismodell" },
    ],
  };
  // Sysadmin-only Bereich
  if (rolle === "sysadmin") {
    nav["System (Sysadmin)"] = [
      { to: "/admin/benutzer", icon: UserCog, label: "Benutzerverwaltung" },
      { to: "/admin/backup", icon: HardDrive, label: "Backup & Restore" },
      { to: "/admin/ftp", icon: Server, label: "FTP-Einstellungen" },
      { to: "/admin/smtp", icon: Mail, label: "E-Mail (SMTP)" },
      { to: "/admin/api-keys", icon: KeyRound, label: "API-Keys (ERP)" },
      { to: "/admin/einstellungen", icon: Sliders, label: "System-Einstellungen" },
      { to: "/admin/auditlog", icon: ClipboardList, label: "Audit-Log" },
    ];
  }
  return nav;
};

const RolleLabel = { admin: "Molkerei (Admin)", sysadmin: "System-Administrator", lieferant: "Lieferant", berater: "HofBerater" };

export const AppShell = ({ children }) => {
  const { user, logout, liefernummer, wechsleLiefernummer } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const nav = getNav(user?.rolle);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const SidebarInhalt = ({ onNavigate }) => (
    <div className="flex h-full flex-col">
      <div className="px-4 py-5">
        <div className="flex items-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary text-primary-foreground font-bold text-lg" style={{ fontFamily: "var(--font-heading)" }}>
            S
          </div>
          <div>
            <p className="font-semibold leading-tight" style={{ fontFamily: "var(--font-heading)" }}>seifried.app</p>
            <p className="text-xs text-muted-foreground">Molkerei Seifried GmbH</p>
          </div>
        </div>
      </div>
      <Separator />
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-5">
        {Object.entries(nav).map(([gruppe, items]) => (
          <div key={gruppe}>
            <p className="px-3 pb-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{gruppe}</p>
            <div className="space-y-0.5">
              {items.map((item) => (
                <NavItem key={item.to} {...item} onClick={onNavigate} />
              ))}
            </div>
          </div>
        ))}
      </nav>
      <Separator />
      <div className="p-3 space-y-2">
        <InstallPromptCard />
        <div className="rounded-lg bg-secondary px-3 py-2">
          <p className="text-sm font-medium truncate">{user?.name}</p>
          <p className="text-xs text-muted-foreground">{RolleLabel[user?.rolle] || user?.rolle}</p>
        </div>
        <Button data-testid="logout-button" variant="outline" size="sm" className="w-full" onClick={handleLogout}>
          <LogOut className="h-4 w-4 mr-2" /> Abmelden
        </Button>
      </div>
    </div>
  );

  const bottomNav = (nav.Portal || []).slice(0, 4);

  return (
    <div className="min-h-screen bg-background">
      {/* PWA Banner (Update / Offline) */}
      <UpdateBanner />
      <OfflineBanner />
      <IosInstallHintDialog />

      {/* Desktop Sidebar */}
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 border-r bg-card lg:block">
        <SidebarInhalt />
      </aside>

      {/* Content-Spalte (flex-column, damit Footer unten "sticky" wird, wenn Content kurz ist) */}
      <div className="flex min-h-screen flex-col lg:pl-64">
        {/* Topbar */}
        <header className="sticky top-0 z-20 flex h-14 items-center gap-3 border-b bg-card/95 backdrop-blur px-4">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button data-testid="mobile-menu-button" variant="ghost" size="icon" className="lg:hidden">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 p-0">
              <SheetTitle className="sr-only">Navigation</SheetTitle>
              <SidebarInhalt onNavigate={() => setMobileOpen(false)} />
            </SheetContent>
          </Sheet>

          <div className="flex-1" />

          {user?.rolle === "lieferant" && user?.liefernummern?.length > 0 && (
            <Select value={liefernummer} onValueChange={wechsleLiefernummer}>
              <SelectTrigger data-testid="supplier-number-switcher-select" className="w-[200px] bg-card">
                <Tractor className="h-4 w-4 mr-1 text-muted-foreground" />
                <SelectValue placeholder="Liefernummer" />
              </SelectTrigger>
              <SelectContent>
                {user.liefernummern.map((nr) => (
                  <SelectItem key={nr} value={nr}>
                    Liefernr. {nr}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </header>

        {/* Content – wächst, um Footer unten zu halten */}
        <main className="flex-1 pb-20 lg:pb-0">
          <div key={location.pathname} className="page-enter mx-auto max-w-screen-2xl px-4 sm:px-6 lg:px-8 py-6">
            {children}
          </div>
        </main>

        {/* Footer – Sibling von <main>, daher immer unten */}
        <footer className="border-t bg-card/60 text-xs text-muted-foreground py-4">
          <div className="mx-auto max-w-screen-2xl px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center sm:justify-between gap-2 text-center sm:text-left">
            <span>© {new Date().getFullYear()} seifried.app · Molkerei Seifried GmbH</span>
            <div className="flex items-center gap-4">
              <NavLink to="/impressum" className="hover:text-primary hover:underline" data-testid="footer-impressum-link">
                Impressum
              </NavLink>
              <NavLink to="/datenschutz" className="hover:text-primary hover:underline" data-testid="footer-datenschutz-link">
                Datenschutz
              </NavLink>
            </div>
          </div>
        </footer>
      </div>

      {/* Mobile Bottom Nav */}
      <nav className="fixed bottom-0 inset-x-0 z-30 flex border-t bg-card lg:hidden">
        {bottomNav.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            data-testid={`bottomnav-${item.to.replace(/\//g, "-").replace(/^-/, "") || "dashboard"}`}
            className={({ isActive }) =>
              `flex flex-1 flex-col items-center gap-0.5 py-2.5 text-[10px] font-medium transition-colors ${
                isActive ? "text-primary" : "text-muted-foreground"
              }`
            }
          >
            <item.icon className="h-5 w-5" />
            {item.label}
          </NavLink>
        ))}
        <button
          data-testid="bottomnav-mehr"
          onClick={() => setMobileOpen(true)}
          className="flex flex-1 flex-col items-center gap-0.5 py-2.5 text-[10px] font-medium text-muted-foreground"
        >
          <MoreHorizontal className="h-5 w-5" />
          Mehr
        </button>
      </nav>
    </div>
  );
};
